<template>
  <div class="relative">
    <div v-if="loading" class="loading-overlay"></div>
    <table class="table table--regular">
      <tbody>
        <slot name="headers" :headers="headers">
          <tr>
            <th v-if="selectedItems">
              <template v-if="canSelectMultiple && items.length">
                <input
                  :checked="allSelected"
                  :id="`${tableId}-toggle-all`"
                  type="checkbox"
                  @change="toggleAll"
                /><label :for="`${tableId}-toggle-all`"></label>
              </template>
            </th>
            <th
              v-for="header in headers"
              :key="header.value"
              :class="getHeaderClasses(header)"
              @click="toggleSorting(header)"
            >
              {{ header.text }}
              <i v-if="sortingMap[header.value] === 'asc'" class="glyphicon glyphicon-arrow-up"></i>
              <i
                v-else-if="sortingMap[header.value] === 'desc'"
                class="glyphicon glyphicon-arrow-down"
              ></i>
            </th>
          </tr>
        </slot>
        <tr v-if="!items.length && loading">
          <td colspan="100%" class="text-center">{{ $t('common.loading') }}...</td>
        </tr>
        <tr v-else-if="!items.length && !loading">
          <td colspan="100%" class="text-center">
            {{ $t('common.noData') }}
          </td>
        </tr>
        <slot :items="items" :headers="headers">
          <tr
            v-for="item in items"
            :key="item[itemKey]"
            :class="{
              clickable: onRowClick,
              selected: selectedItemKeySet.has(item[itemKey]),
              'h-active': highlightRow && highlightRow(item),
            }"
            @click="onRowClick && onRowClick(item)"
          >
            <td v-if="selectedItems" data-label="">
              <input
                :checked="selectedItemKeySet.has(item[itemKey])"
                :id="`${tableId}-item-${item[itemKey]}`"
                type="checkbox"
              /><label
                :for="`${tableId}-item-${item[itemKey]}`"
                @click.stop.prevent="toggleItem(item)"
              ></label>
            </td>
            <td v-for="header in headers" :key="header.value" :data-label="header.text">
              <slot :name="`item.${header.value}`" :item="item" :header="header">
                {{ getProperty(item, header.value) }}
              </slot>
            </td>
          </tr>
        </slot>
      </tbody>
    </table>
    <BasePagination
      v-if="pagination"
      :current-page="pagination.page"
      :total-items="pagination.total"
      :page-size="pagination.pageSize"
      @update:page="$emit('update:page', $event)"
    />
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import BasePagination from './BasePagination';

export default {
  name: 'BaseTable',

  components: { BasePagination },

  model: {
    prop: 'selectedItems',
    event: 'input',
  },

  props: {
    headers: {
      type: Array,
      default: () => [],
    },

    highlightRow: {
      type: Function,
      default: null,
    },

    items: {
      type: Array,
      required: true,
    },

    itemKey: {
      type: String,
      default: 'id',
    },

    loading: {
      type: Boolean,
      default: false,
    },

    enableSorting: {
      type: Boolean,
      default: false,
    },

    onRowClick: {
      type: Function,
      default: null,
    },

    pagination: {
      type: Object,
      default: () => null,
    },

    selectedItems: {
      type: Array,
      default: () => null,
    },
  },

  data() {
    return {
      sortingArray: [],
      tableId: uuidv4(),
    };
  },

  computed: {
    allSelected() {
      if (!this.selectedItems || this.items.length === 0) {
        return false;
      }
      return this.items.length === this.selectedItems.length;
    },

    canSelectMultiple() {
      if (this.selectedItems === null) {
        return false;
      }
      return this.selectedItems.length >= 0;
    },

    selectedItemKeySet() {
      return new Set(this.selectedItems?.map((item) => item[this.itemKey]));
    },

    // for more efficient lookup, so we don't have to dig in the array every time
    sortingMap() {
      const map = {};
      this.sortingArray.forEach((header) => {
        map[header.value] = header.sortingDirection;
      });
      return map;
    },
  },

  methods: {
    getHeaderClasses(header) {
      const classes = [];
      if (this.enableSorting && !this.loading) {
        classes.push('clickable');
      }
      if (header.classes) {
        classes.push(header.classes);
      }
      return classes;
    },

    getProperty(object, propertyName) {
      // using this function instead of just object[propertyName] to support nested properties like qualification.type.name
      const parts = propertyName.split('.');
      let property = object || this;

      for (let i = 0; i < parts.length; i += 1) {
        if (!property) {
          return '';
        }
        property = property[parts[i]];
      }

      return property;
    },

    toggleAll() {
      const newValue = this.allSelected ? [] : [...this.items];
      this.$emit('input', newValue);
    },

    toggleItem(toggledItem) {
      let selected;
      if (this.canSelectMultiple) {
        selected = this.selectedItemKeySet.has(toggledItem[this.itemKey])
          ? this.selectedItems.filter((item) => item[this.itemKey] !== toggledItem[this.itemKey])
          : [...this.selectedItems, toggledItem];
      } else {
        selected = this.selectedItemKeySet.has(toggledItem[this.itemKey])
          ? this.selectedItems.filter((item) => item[this.itemKey] !== toggledItem[this.itemKey])
          : [toggledItem];
      }
      this.$emit('input', selected);
    },

    toggleSorting(header) {
      if (this.loading || !this.enableSorting) {
        return;
      }

      if (!this.sortingMap[header.value]) {
        // if header is not being sorted, sort it last by asc
        this.sortingArray.push({
          value: header.value,
          sortingDirection: 'asc',
        });
      } else if (this.sortingMap[header.value] === 'asc') {
        // if header is being sorted by asc, switch it to desc
        for (let i = 0; i < this.sortingArray.length; i += 1) {
          if (this.sortingArray[i].value === header.value) {
            const newValue = {
              value: header.value,
              sortingDirection: 'desc',
            };
            this.$set(this.sortingArray, i, newValue);
            break;
          }
        }
      } else {
        // if the header is being sorted by desc, remove sortingArray by that header
        this.sortingArray = this.sortingArray.filter((h) => h.value !== header.value);
      }
      // build the format that the API expects
      const sortingString = this.sortingArray
        .map((h) => `${h.value} ${h.sortingDirection}`)
        .join(',');
      this.$emit('update:sorting', sortingString);
    },
  },
};
</script>
